import { useState, useEffect, useCallback } from 'react';

function ShowMultiImageModal({ images = [], onClose, isOpen = false, startIndex = 0, isRTL = false,isPrefix = false }) {
  const [currentIndex, setCurrentIndex] = useState(startIndex || 0);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [showCaptions, setShowCaptions] = useState(false);

  // Sync when opened or startIndex changes
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(Math.max(0, Math.min(startIndex || 0, images?.length - 1)));
      setIsZoomed(false);
      setPosition({ x: 0, y: 0 });
      // prevent body scroll
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, startIndex, images?.length]);

  // keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (isZoomed) {
          setIsZoomed(false);
          setPosition({ x: 0, y: 0 });
        } else {
          onClose();
        }
      }
      if (e.key === 'ArrowRight') (isRTL ? goToPrevious() : goToNext());
      if (e.key === 'ArrowLeft') (isRTL ? goToNext() : goToPrevious());
      if (e.key === ' ' || e.key === 'Enter') toggleZoom();
      if (e.key === 'c') setShowCaptions(prev => !prev);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentIndex, isOpen, isRTL, images?.length, isZoomed]);

  const goToPrevious = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex === 0 ? images?.length - 1 : prevIndex - 1));
    setIsZoomed(false);
    setPosition({ x: 0, y: 0 });
    
    setTimeout(() => setIsTransitioning(false), 300);
  }, [images?.length, isTransitioning]);

  const goToNext = useCallback(() => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex((prevIndex) => (prevIndex === images?.length - 1 ? 0 : prevIndex + 1));
    setIsZoomed(false);
    setPosition({ x: 0, y: 0 });
    
    setTimeout(() => setIsTransitioning(false), 300);
  }, [images?.length, isTransitioning]);

  const selectImage = useCallback((index) => {
    if (isTransitioning) return;
    
    setIsTransitioning(true);
    setCurrentIndex(index);
    setIsZoomed(false);
    setPosition({ x: 0, y: 0 });
    
    setTimeout(() => setIsTransitioning(false), 300);
  }, [isTransitioning]);

  const toggleZoom = useCallback(() => {
    if (isZoomed) {
      setIsZoomed(false);
      setPosition({ x: 0, y: 0 });
    } else {
      setIsZoomed(true);
    }
  }, [isZoomed]);

  const handleWheel = useCallback((e) => {
    if (isZoomed) {
      e.preventDefault();
      setPosition(prev => ({
        x: prev.x - e.deltaX,
        y: prev.y - e.deltaY
      }));
    }
  }, [isZoomed]);

  const handleMouseDown = useCallback((e) => {
    if (isZoomed) {
      e.preventDefault();
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }, [isZoomed, position]);

  const handleMouseMove = useCallback((e) => {
    if (isZoomed && dragStart) {
      e.preventDefault();
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  }, [isZoomed, dragStart]);

  const handleMouseUp = useCallback(() => {
    setDragStart(null);
  }, []);

  if (!isOpen) return null;
  if (!images || images?.length === 0) return null;

  const currentImage = images[currentIndex];
  const imageSrc = typeof currentImage === 'object' 
    ? isPrefix? currentImage.src :process.env.REACT_APP_IMG_URL + currentImage
    :  isPrefix?currentImage:process.env.REACT_APP_IMG_URL + currentImage;
  
  const caption = typeof currentImage === 'object' ? currentImage.caption : '';

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-95 z-50 flex items-center justify-center p-4 transition-opacity duration-300"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      dir={isRTL ? 'rtl' : 'ltr'}
      onWheel={handleWheel}
      onMouseUp={handleMouseUp}
    >
      <div className="relative w-full max-w-6xl h-full max-h-[90vh] flex flex-col bg-transparent">
        {/* Header */}
        <div className="flex items-center justify-between text-white mb-4 px-2 py-3 bg-gray-800 bg-opacity-50 rounded-lg">
          <div className="text-lg font-medium select-none flex items-center">
            <span className="bg-teal-500 rounded-full w-8 h-8 flex items-center justify-center mr-2">
              {currentIndex + 1}
            </span>
            
          </div>

          <div className="flex items-center gap-3">
            {/* Caption toggle */}
            {caption && (
              <button
                onClick={() => setShowCaptions(prev => !prev)}
                className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors tooltip"
                aria-label={showCaptions ? "Hide caption" : "Show caption"}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="tooltip-text">{showCaptions ? "Hide Caption" : "Show Caption"}</span>
              </button>
            )}
            
            {/* Download button */}
            <a 
              href={imageSrc} 
              download 
              className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors tooltip"
              aria-label="Download image"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              <span className="tooltip-text">Download</span>
            </a>
            
            {/* Zoom button */}
            <button
              onClick={toggleZoom}
              className="p-2 rounded-full bg-gray-700 hover:bg-gray-600 transition-colors tooltip"
              aria-label={isZoomed ? "Zoom out" : "Zoom in"}
            >
              {isZoomed ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3-3H7" />
                </svg>
              )}
              <span className="tooltip-text">{isZoomed ? "Zoom Out" : "Zoom In"}</span>
            </button>
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="p-2 rounded-full bg-red-500 hover:bg-red-600 transition-colors tooltip"
              aria-label="Close modal"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              <span className="tooltip-text">Close</span>
            </button>
          </div>
        </div>

        {/* Main image container */}
        <div className="relative flex-1 flex items-center justify-center overflow-hidden rounded-lg bg-black bg-opacity-40">
          <div 
            className="relative w-full h-full flex items-center justify-center transition-transform duration-300 ease-in-out"
            style={{ 
              cursor: isZoomed ? 'grab' : 'zoom-in',
              transform: `translate(${position.x}px, ${position.y}px)`
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <div className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}>
              <img
                src={imageSrc}
                alt={`Image ${currentIndex + 1}`}
                className="w-full h-full object-contain"
                onClick={toggleZoom}
                style={{ transform: isZoomed ? 'scale(2)' : 'scale(1)' }}
                role="img"
              />
            </div>
            
            {/* Image caption */}
            {caption && showCaptions && (
              <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white p-3 text-center transition-opacity duration-300">
                {caption}
              </div>
            )}

            {/* Loading indicator */}
            {isTransitioning && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
              </div>
            )}

            {/* Navigation arrows */}
            {images?.length > 1 && (
              <>
                <button
                  onClick={goToPrevious}
                  className="absolute left-4 p-3 rounded-full bg-black bg-opacity-60 text-white hover:bg-opacity-80 transition-all transform hover:scale-110 shadow-lg"
                  aria-label="Previous image"
                  style={{ left: isRTL ? 'auto' : 16, right: isRTL ? 16 : 'auto' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
                  </svg>
                </button>

                <button
                  onClick={goToNext}
                  className="absolute right-4 p-3 rounded-full bg-black bg-opacity-60 text-white hover:bg-opacity-80 transition-all transform hover:scale-110 shadow-lg"
                  aria-label="Next image"
                  style={{ right: isRTL ? 'auto' : 16, left: isRTL ? 16 : 'auto' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={isRTL ? "M15 19l-7-7 7-7" : "M9 5l7 7-7 7"} />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Thumbnails */}
        {images?.length > 1 && (
          <div className="mt-4 flex items-center justify-center gap-2 py-2 overflow-x-auto">
            {(isRTL ? [...images].reverse() : images).map((img, revIndex) => {
              const index = isRTL ? images?.length - 1 - revIndex : revIndex;
              const thumbSrc = typeof img === 'object' 
                ? isPrefix? img.thumb || img.src : process.env.REACT_APP_IMG_URL + img.thumb || img.src 
                : isPrefix? img : process.env.REACT_APP_IMG_URL + img;
              
              return (
                <div
                  key={index}
                  className={`flex-shrink-0 cursor-pointer border-2 rounded-lg overflow-hidden transition-all transform hover:scale-110 ${
                    index === currentIndex 
                      ? 'border-teal-400 scale-110 ring-2 ring-teal-300 ring-opacity-50' 
                      : 'border-transparent hover:border-teal-200'
                  }`}
                  onClick={() => selectImage(index)}
                  style={{ width: '80px', height: '60px' }}
                >
                  <img
                    src={thumbSrc}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Custom CSS for tooltips and transitions */}
      <style jsx>{`
        .tooltip {
          position: relative;
        }
        .tooltip-text {
          visibility: hidden;
          background-color: rgba(0, 0, 0, 0.8);
          color: white;
          text-align: center;
          border-radius: 4px;
          padding: 5px 10px;
          position: absolute;
          z-index: 1;
          bottom: 125%;
          left: 50%;
          transform: translateX(-50%);
          opacity: 0;
          transition: opacity 0.3s;
          white-space: nowrap;
        }
        .tooltip:hover .tooltip-text {
          visibility: visible;
          opacity: 1;
        }
      `}</style>
    </div>
  );
}

export default ShowMultiImageModal;